import { useState } from 'react';

import { Plus, Pencil, Trash2, Send, ScrollText } from 'lucide-react';

import { DeleteConfirmationDialog } from '@/components/shared/DeleteConfirmationDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  useWebhooks,
  useCreateWebhook,
  useUpdateWebhook,
  useDeleteWebhook,
  useTriggerWebhook,
} from '@/hooks/useWebhooks';

import { DeliveriesDialog } from './DeliveriesDialog';
import { TriggerWebhookDialog } from './TriggerWebhookDialog';
import { WebhookFormDialog } from './WebhookFormDialog';

import type { Webhook, CreateWebhookDto, UpdateWebhookDto } from '@/lib/api/webhooks.api';

export const WebhooksPage = () => {
  usePageTitle('Webhooks');
  const { data: webhooks, isLoading } = useWebhooks();
  const createMutation = useCreateWebhook();
  const updateMutation = useUpdateWebhook();
  const deleteMutation = useDeleteWebhook();
  const triggerMutation = useTriggerWebhook();
  const { toast } = useToast();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Webhook | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Webhook | null>(null);
  const [triggerTarget, setTriggerTarget] = useState<Webhook | null>(null);
  const [deliveriesTarget, setDeliveriesTarget] = useState<Webhook | null>(null);
  const [deliveriesPage, setDeliveriesPage] = useState(1);
  const [formKey, setFormKey] = useState(0);
  const [triggerKey, setTriggerKey] = useState(0);

  const handleCreate = () => {
    setEditTarget(null);
    setFormKey((k) => k + 1);
    setIsFormOpen(true);
  };

  const handleEdit = (webhook: Webhook) => {
    setEditTarget(webhook);
    setFormKey((k) => k + 1);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreateWebhookDto | UpdateWebhookDto) => {
    if (editTarget) {
      await updateMutation.mutateAsync({ id: editTarget.id, data });
      toast({ variant: 'success', title: 'Webhook updated' });
    } else {
      await createMutation.mutateAsync(data as CreateWebhookDto);
      toast({ variant: 'success', title: 'Webhook created' });
    }
    setIsFormOpen(false);
    setEditTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
      toast({ variant: 'success', title: 'Webhook deleted' });
    } catch {
      toast({ variant: 'destructive', title: 'Failed to delete webhook' });
    }
  };

  const handleTrigger = async (body: {
    dataType: 'availability_data' | 'lead_data' | 'shift_data';
    fromDate: string;
    toDate: string;
    confirmedOnly?: boolean;
  }) => {
    if (!triggerTarget) return;
    const result = await triggerMutation.mutateAsync({ id: triggerTarget.id, body });
    setTriggerTarget(null);
    toast({
      variant: 'success',
      title: 'Webhook triggered',
      description: `Delivery queued with ${result.callsFound} call(s).`,
    });
  };

  const openDeliveries = (webhook: Webhook) => {
    setDeliveriesPage(1);
    setDeliveriesTarget(webhook);
  };

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Webhooks</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Manage webhook endpoints for event notifications
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Webhook
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Webhooks</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          ) : !webhooks || webhooks.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-muted-foreground mb-4 text-sm">No webhooks configured yet.</p>
              <Button variant="outline" onClick={handleCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Create your first webhook
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500 dark:text-gray-400">
                    <th className="pr-4 pb-3 font-medium">Name</th>
                    <th className="pr-4 pb-3 font-medium">URL</th>
                    <th className="pr-4 pb-3 font-medium">Events</th>
                    <th className="pr-4 pb-3 font-medium">Status</th>
                    <th className="pb-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {webhooks.map((webhook) => (
                    <tr key={webhook.id} className="border-b last:border-b-0 dark:border-slate-700">
                      <td className="py-3 pr-4 font-medium">{webhook.name}</td>
                      <td className="py-3 pr-4">
                        <code className="text-muted-foreground block max-w-[300px] truncate text-xs">
                          {webhook.url}
                        </code>
                      </td>
                      <td className="py-3 pr-4">
                        {webhook.events.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {webhook.events.map((event) => (
                              <code
                                key={event}
                                className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-slate-800"
                              >
                                {event}
                              </code>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">Manual only</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            webhook.active
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-gray-400'
                          }`}
                        >
                          {webhook.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeliveries(webhook)}
                            title="View deliveries"
                          >
                            <ScrollText className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setTriggerKey((k) => k + 1);
                              setTriggerTarget(webhook);
                            }}
                            title="Manual trigger"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(webhook)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(webhook)}
                            title="Delete"
                            className="text-red-600 hover:text-red-700 dark:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <WebhookFormDialog
        key={formKey}
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setEditTarget(null);
        }}
        onSubmit={handleFormSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
        webhook={editTarget}
      />

      <TriggerWebhookDialog
        key={triggerKey}
        open={!!triggerTarget}
        onOpenChange={(open) => {
          if (!open) setTriggerTarget(null);
        }}
        onSubmit={handleTrigger}
        isPending={triggerMutation.isPending}
        webhook={triggerTarget}
      />

      <DeliveriesDialog
        open={!!deliveriesTarget}
        onOpenChange={(open) => {
          if (!open) setDeliveriesTarget(null);
        }}
        webhook={deliveriesTarget}
        page={deliveriesPage}
        onPageChange={setDeliveriesPage}
      />

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        resourceName="webhook"
        resourceIdentifier={deleteTarget?.name}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
